import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, map, shareReplay, finalize, of } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../../../../../../projects/my-app/src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  protected router = inject(Router);
  // simple in-memory map to deduplicate identical concurrent requests
  protected inProgressRequests: Map<string, Observable<any>> = new Map();


  /**
   * In-flight request cache.
   * Key = method + URL + payload string + query params.
   */
  private inFlightRequests = new Map<string, Observable<any>>();

  private resolveHeaders(data?: any, filters?: any): Record<string, string> {
    const providedHeaders = {
      ...(data?.headers || {}),
      ...(filters?.headers || {}),
    } as Record<string, string>;

    if (!providedHeaders['X-TENANT-ID'] && !providedHeaders['x-tenant-id']) {
      providedHeaders['X-TENANT-ID'] = 'tenantA';
    }

    return providedHeaders;
  }

  constructor(protected http: HttpClient) { }

  /** Exclude internal keys (UI state, widget records) from API request params */
  private static readonly INTERNAL_KEYS = new Set(['loading']);
  protected cleanRequestParams(params: any): any {
    if (!params) return {};
    const cleaned: any = {};
    for (const key of Object.keys(params)) {
      if (ConfigService.INTERNAL_KEYS.has(key)) continue;
      if (key.startsWith('widget-')) continue;
      cleaned[key] = params[key];
    }
    return cleaned;
  }

  getDashboardConfig(configFileName: string): Observable<any> {
    return this.http.get(`/assets/dashboard-config/${configFileName}`);
  }

  protected buildPayload(requestParamFilters: any): any {
    const currentPath = this.router.url.split('?')[0]; // Remove query params
    const dashboardName = currentPath.startsWith('/') ? currentPath.substring(1) : currentPath;
    if (
      dashboardName === 'europe-impact-report' &&
      (!requestParamFilters || Object.keys(requestParamFilters).length === 0)
    ) {
      return {
        filter: {
          where: [],
          join: {},
          orderBy: {},
        },
      };
    }
    let staticFilters: any[] = [];
    const customerNameValue = requestParamFilters?.['customer_name'];

    if (dashboardName === 'customer-overview') {
      // added condition to filter out default customername in payload if customer name is selected
      if (
        !customerNameValue &&
        (!Array.isArray(customerNameValue) || customerNameValue.length > 0)
      ) {
        staticFilters = [
          {
            attributeName: 'customer_name',
            operator: '!=',
            attributeValues: ['NA'],
            logicalOperator: 'AND',
          },
        ];
      }
    } else if (dashboardName === 'cspec-bridge') {
      if (
        !customerNameValue &&
        (!Array.isArray(customerNameValue) || customerNameValue.length > 0)
      ) {
        staticFilters = [
          {
            attributeName: 'latest_shop_order_flag',
            operator: '=',
            attributeValues: [true],
            logicalOperator: 'AND',
          },
        ];
      }
    } else if (
      dashboardName !== 'opportunity-overview' &&
      dashboardName !== 'europe-impact-report'
    ) {
      if (
        !customerNameValue &&
        (!Array.isArray(customerNameValue) || customerNameValue.length > 0)
      ) {
        staticFilters = [
          {
            attributeName: 'customer_name',
            operator: '!=',
            attributeValues: ['NA'],
            logicalOperator: 'AND',
          },
          {
            attributeName: 'latest_shop_order_flag',
            operator: '=',
            attributeValues: [true],
            logicalOperator: 'AND',
          },
        ];
      }
    }

    const dynamicFilters = Object.entries(requestParamFilters)
      .map(([key, value]) => {
        const rawValues = Array.isArray(value) ? value.flat(Infinity) : [value]; // recursively flatten the array to any depth eg. [[[]]].
        let updatedValues = rawValues.filter(
          (v) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0),
        );

        if (updatedValues?.length === 0) return null;

        return {
          attributeName: key,
          operator: updatedValues.length > 1 ? 'IN' : '=',
          attributeValues: updatedValues,
          logicalOperator: 'AND',
        };
      })
      .filter((filter): filter is NonNullable<typeof filter> => filter !== null); //emoves null entries from the array.

    // Adjust logicalOperator handling
    if (dynamicFilters.length > 0) {
      dynamicFilters[dynamicFilters.length - 1].logicalOperator = '';
      if (staticFilters.length > 0) {
        staticFilters[staticFilters.length - 1].logicalOperator = 'AND';
      }
    } else {
      if (staticFilters.length > 0) {
        staticFilters[staticFilters.length - 1].logicalOperator = '';
      }
    }

    return {
      filter: {
        where: [...staticFilters, ...dynamicFilters],
        join: {},
        orderBy: {},
      },
    };
  }

  protected buildQueryParams(queryParamFilters: any): HttpParams {
    let params = new HttpParams();
    for (const key in queryParamFilters) {
      const value = queryParamFilters[key];

      if (key === 'slider_range' && Array.isArray(value) && value.length === 2) {
        const start = parseInt(value[0], 10);
        const end = parseInt(value[1], 10);

        params = params.set('startRange', start.toString());
        params = params.set('endRange', end.toString());
      } else if (key === 'button') {
        params = params.set(value[0], true);
      } else {
        params = params.set(key, Array.isArray(value) ? value.join(',') : value);
      }
    }

    return params;
  }

  getConfig(
    url: string,
    data?: any,
    filters?: any,
    method?: string,
    isTable = false,
  ): Observable<any> {

    if (!url) {
      return of(this.unwrapResponse(data?.data ?? data?.widgetData ?? data ?? {}));
    }

    const requestHeaders = this.resolveHeaders(data, filters);

    const queryParamsForHttp = { ...(filters?.queryParams || {}) };
    const rawQueryString = filters?.rawQueryString;

    // Embed Query Params into URL
    if (data?.embedQueryParams && Array.isArray(data.embeddedParams)) {
      const embeddedParts: string[] = [];
      for (const paramName of data.embeddedParams) {
        if (queryParamsForHttp[paramName]) {
          embeddedParts.push(queryParamsForHttp[paramName]);
          delete queryParamsForHttp[paramName];
        }
      }
      if (embeddedParts.length > 0) {
        const baseUrl = url.endsWith('/') ? url.slice(0, -1) : url;
        url = [baseUrl, ...embeddedParts].join('/');
      }
    }

    // Payload Logic
    let payload: any = null;
    const defaultRequestData =
      data?.defaultRequestData || data?.defaultRequesData || data?.defaultPayload || {};

    const filteredRequestParams = this.cleanRequestParams(filters?.requestParams);
    const excludeKeys = new Set(data?.excludeFilterKeys || []);
    const filteredWithExclusions = Object.fromEntries(
      Object.entries(filteredRequestParams).filter(([key]) => !excludeKeys.has(key))
    );
    const hasFilteredRequestParams = Object.keys(filteredWithExclusions).length > 0;

    if (data?.skipDefaultPayload) {
      // When skipDefaultPayload is true, still merge filters into customPayload
      payload = JSON.parse(JSON.stringify(data?.customPayload || {}));

      // Merge request params into the payload's requestData
      if (hasFilteredRequestParams && payload.requestData) {
        payload.requestData = {
          ...payload.requestData,
          ...filteredWithExclusions,
        };
      }
    } else {
      payload = {
        requestData: {
          ...defaultRequestData,
          ...filteredWithExclusions,
          ...(data?.customPayload || {}),
        },
        responseList: [],
        entity: data?.entity || 'default_entity',
        workflowId: data?.workflowId || 'default_workflow',
        endpoints: [
          {
            functionName: data?.functionName || 'DefaultService',
            version: data?.version || '1.0',
          },
        ],
        communicationType: 'rest',
        version: data?.version || '1.0',
      };
    }

    // Query Params Logic
    const useQueryParams = filters?.useQueryParams === true || data?.useQueryParam === true;
    let queryParams = new HttpParams();

    if (useQueryParams) {
      if (rawQueryString) {
        url = `${url}?${rawQueryString}`;
      } else {
        queryParams = this.buildQueryParams(queryParamsForHttp);
      }
    }

    if (filters && 'useQueryParams' in filters) {
      delete filters.useQueryParams;
    }

    if (!!isTable) {
      // Table specific logic if needed in future
    }

    // Prepend Base URL if relative (skip for local assets)
    const isLocalAssets = url.startsWith('assets') || url.startsWith('/assets');
    if (!isLocalAssets) {
      const baseUrl = environment.URL.endsWith('/')
        ? environment.URL.slice(0, -1)
        : environment.URL;
      const path = url.startsWith('/') ? url : `/${url}`;
      url = `${baseUrl}${path}`;
    }

    // Determine HTTP Method
    const requestMethod = method || data?.method || 'post';

    const dedupKey = `${requestMethod.toUpperCase()}|${url}|${JSON.stringify(payload)}|${queryParams.toString()}`;
    if (this.inProgressRequests.has(dedupKey)) {
      return this.inProgressRequests.get(dedupKey)!;
    }

    const req$ = (
      requestMethod === 'post'
        ? this.http.post<any>(url, payload, { params: queryParams, headers: requestHeaders })
        : this.http.get<any>(url, { params: queryParams, headers: requestHeaders })
    ).pipe(
      map((res) => this.unwrapResponse(res)),
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => this.inProgressRequests.delete(dedupKey)),
    );

    this.inProgressRequests.set(dedupKey, req$);
    return req$;
  }

  createPayload(
    filters: { [key: string]: any } = {},
    entity: string = '',
    filterName: string = '',
  ): any {
    const keys = Object.keys(filters);

    const where = keys.flatMap((key) => {
      const value = filters[key];

      // Check if the key is 'filter' and its value is an array of rule objects
      const isFilterRuleArray =
        key === 'filter' &&
        Array.isArray(value) &&
        value.length > 0 &&
        typeof value[0] === 'object' &&
        value[0] !== null &&
        'attributeName' in value[0];

      if (isFilterRuleArray) {
        return value; // It's from the dialog, return the rules directly
      }

      // Process other simple filters (or a 'filter' that is not a rule array)
      const values = (Array.isArray(value) ? value : [value]).filter(
        (v) => v !== null && v !== undefined && v !== '' && v !== 'All',
      );

      if (values.length === 0) {
        return [];
      }

      return [
        {
          attributeName: key,
          operator: values.length > 1 ? 'IN' : '=',
          attributeValues: values,
          logicalOperator: 'AND', // Placeholder
        },
      ];
    });

    // Now, fix the logical operators for the entire 'where' clause
    if (where.length > 0) {
      for (let i = 0; i < where.length - 1; i++) {
        // If a rule from the dialog already has an operator (like OR), respect it.
        // Otherwise, set it to AND.
        if (!where[i].logicalOperator) {
          where[i].logicalOperator = 'AND';
        }
      }
      // The very last item in the whole clause has no logical operator.
      where[where.length - 1].logicalOperator = '';
    }

    return {
      filterName,
      entity,
    };
  }

  getApi(url: string, filters?: any, data?: any, method: 'post' | 'get' = 'post'): Observable<any> {
    const queryParamsForHttp = { ...(filters?.queryParams || {}) };
    const rawQueryString = filters?.rawQueryString;
    const requestHeaders = this.resolveHeaders(data, filters);

    //  Embed query params into URL path
    if (data?.embedQueryParams && Array.isArray(data.embeddedParams)) {
      const embeddedParts: string[] = [];

      for (const paramName of data.embeddedParams) {
        if (queryParamsForHttp[paramName]) {
          embeddedParts.push(queryParamsForHttp[paramName]);
          delete queryParamsForHttp[paramName];
        }
      }

      if (embeddedParts.length > 0) {
        const baseUrl = url.endsWith('/') ? url.slice(0, -1) : url;
        url = [baseUrl, ...embeddedParts].join('/');
      }
    }

    //  PAYLOAD LOGIC
    let payload: any = null;

    // Use defaultPayload only if no dynamic requestParams are provided
    const filteredReqParams = this.cleanRequestParams(filters?.requestParams);
    const hasFilteredReqParams = Object.keys(filteredReqParams).length > 0;
    const defaultRequestData =
      data?.defaultRequestData || data?.defaultRequesData || data?.defaultPayload || {};

    if (data?.skipDefaultPayload) {
      //  Do NOT use base payload
      // Send only custom payload or empty object
      payload = data?.customPayload || {};
    } else {
      //  USE BASE PAYLOAD
      payload = {
        requestData: {
          ...(!hasFilteredReqParams ? defaultRequestData : {}),
          ...filteredReqParams,
          ...(data?.customPayload || {}),
        },
        responseList: [],
        entity: data?.entity || 'default_entity',
        workflowId: data?.workflowId || 'default_workflow',
        endpoints: [
          {
            functionName: data?.functionName || 'DefaultService',
            version: data?.version || '1.0',
          },
        ],
        communicationType: 'rest',
        version: '1.0',
      };
    }

    // Query Params
    const useQueryParams = data?.useQueryParam !== false;
    let queryParams = new HttpParams();

    if (useQueryParams) {
      if (rawQueryString) {
        url = `${url}?${rawQueryString}`;
      } else {
        queryParams = this.buildQueryParams(queryParamsForHttp);
      }
    }

    // Prepend Base URL if relative (skip for local assets)
    const isLocalAssets = url.startsWith('assets') || url.startsWith('/assets');
    if (!isLocalAssets) {
      const baseUrl = environment.URL.endsWith('/')
        ? environment.URL.slice(0, -1)
        : environment.URL;
      const path = url.startsWith('/') ? url : `/${url}`;
      url = `${baseUrl}${path}`;
    }

    // API Call
    const cacheKey = `${method}|${url}|${JSON.stringify(payload)}|${queryParams.toString()}`;

    if (this.inFlightRequests.has(cacheKey)) {
      return this.inFlightRequests.get(cacheKey)!;
    }

    const request$ = (
      method === 'post'
        ? this.http.post<any>(url, payload, { params: queryParams, headers: requestHeaders })
        : this.http.get<any>(url, { params: queryParams, headers: requestHeaders })
    ).pipe(
      map((res) => this.unwrapResponse(res)),
      shareReplay(1),
      finalize(() => this.inFlightRequests.delete(cacheKey))
    );

    this.inFlightRequests.set(cacheKey, request$);
    return request$;
  }

  getDropdownOptions(
    url: string,
    payload: any,
    method: string = 'post',
    headers?: Record<string, string>,
  ): Observable<any> {
    const isLocalAssets = url.startsWith('assets') || url.startsWith('/assets');
    if (!isLocalAssets) {
      const baseUrl = environment.URL.endsWith('/')
        ? environment.URL.slice(0, -1)
        : environment.URL;
      const path = url.startsWith('/') ? url : `/${url}`;
      url = `${baseUrl}${path}`;
    }
    const normalizedMethod = (method || 'post').toLowerCase();
    const requestHeaders = this.resolveHeaders({ headers });
    const dedupKey = `DROPDOWN|${normalizedMethod.toUpperCase()}|${url}|${JSON.stringify(payload)}|${JSON.stringify(requestHeaders)}`;
    if (this.inProgressRequests.has(dedupKey)) {
      return this.inProgressRequests.get(dedupKey)!;
    }

    const req$ = (
      normalizedMethod === 'get'
        ? this.http.get<any>(url, { headers: requestHeaders })
        : this.http.post<any>(url, payload, { headers: requestHeaders })
    ).pipe(
      map((res) => this.unwrapResponse(res)),
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => this.inProgressRequests.delete(dedupKey)),
    );

    this.inProgressRequests.set(dedupKey, req$);
    return req$;
  }

  protected unwrapResponse(response: any): any {
    if (response?.responseList?.length > 0) {
      const firstItem = response.responseList[0];
      if (Array.isArray(firstItem?.data)) {
       
        return firstItem.data.length === 1 ? firstItem.data[0] : firstItem.data;
      }
    }
    return response;
  }

  getData(url: string) {
    return this.http.get(url);
  }
}
